import {
  DEFAULT_GRID_AUTOSIZE_OPTIONS,
  type GridAutosizeOptions,
  type GridColDef,
  type GridSortModel
} from '@mui/x-data-grid-pro'
import {
  formatUsdPrice,
  formatRoundedCoinAmount,
  formatRoundedUsdAmount,
  roundCoinAmount, roundUsdAmount
} from '../../utils/rounding-formatting-utils'
import clsx from 'clsx'
import { type MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'

const BALANCE_COL_DEF_COIN: GridColDef[] = [
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 70 },
  {
    field: 'account',
    headerName: 'Account',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  { field: 'account_type', headerName: 'Account Type', type: 'string', minWidth: 110 },
  {
    field: 'account_balance',
    headerName: 'Account Balance',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'price_usd',
    headerName: 'Price USD',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatUsdPrice(value),
  },
  {
    field: 'balance_coin',
    headerName: 'Balance Coin',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundCoinAmount(Number(params.value)) > 0,
        negative: roundCoinAmount(Number(params.value)) < 0
      })
    },
  },
  {
    field: 'balance_usd',
    headerName: 'Balance USD',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundUsdAmount(Number(params.value)) > 0,
        negative: roundUsdAmount(Number(params.value)) < 0
      })
    },
  },
  {
    field: 'initial_balance_coin',
    headerName: 'Initial Balance',
    type: 'number',
    minWidth: 50,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'pnl_coin',
    headerName: 'PnL Coin',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'pnl_usd',
    headerName: 'PnL USD',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedUsdAmount(value),
  }
]

const BALANCE_COL_DEF_ACCOUNT: GridColDef[] = [
  {
    field: 'account',
    headerName: 'Account',
    type: 'string',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  { field: 'account_type', headerName: 'Account Type', type: 'string', minWidth: 110 },
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 70 },
  {
    field: 'account_balance',
    headerName: 'Account Balance',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'price_usd',
    headerName: 'Price USD',
    type: 'number',
    minWidth: 60,
    valueFormatter: (value) => formatUsdPrice(value),
  },
  {
    field: 'balance_coin',
    headerName: 'Balance Coin',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'balance_usd',
    headerName: 'Balance USD',
    type: 'number',
    minWidth: 100,
    valueFormatter: (value) => formatRoundedUsdAmount(value),
  },
  {
    field: 'initial_balance_coin',
    headerName: 'Initial Balance',
    type: 'number',
    minWidth: 50,
    valueFormatter: (value) => formatRoundedCoinAmount(value),
  },
  {
    field: 'pnl_coin',
    headerName: 'PnL Coin',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedCoinAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundCoinAmount(Number(params.value)) > 0,
        negative: roundCoinAmount(Number(params.value)) < 0
      })
    },
  },
  {
    field: 'pnl_usd',
    headerName: 'PnL USD',
    type: 'number',
    minWidth: 100,
    sortComparator: (v1, v2) => {
      return Math.abs(v1) > Math.abs(v2) ? 1 : Math.abs(v1) === Math.abs(v2) ? 0 : -1
    },
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        positive: roundUsdAmount(Number(params.value)) > 0,
        negative: roundUsdAmount(Number(params.value)) < 0
      })
    },
  }
]

const BALANCE_GROUPING_COIN_SORTING: GridSortModel = [
  { field: 'coin', sort: 'asc' },
  { field: 'account', sort: 'asc' },
  { field: 'account_type', sort: 'asc' }
]
const BALANCE_GROUPING_ACCOUNT_SORTING: GridSortModel = [
  { field: 'account', sort: 'asc' },
  { field: 'account_type', sort: 'asc' },
  { field: 'coin', sort: 'asc' }
]

export interface BalanceModeOptionsConfig {
  endpoint: string | undefined
  columns: GridColDef[]
  autosizeOptions: GridAutosizeOptions
  sortModel: GridSortModel
}

export const BALANCE_MODE_OPTIONS_CONFIG: Record<MODE_OPTIONS, BalanceModeOptionsConfig> = {
  none: {
    endpoint: undefined,
    columns: [],
    autosizeOptions: DEFAULT_GRID_AUTOSIZE_OPTIONS,
    sortModel: [],
  },
  coin: {
    endpoint: process.env.REACT_APP_API_ASSET,
    columns: BALANCE_COL_DEF_COIN,
    autosizeOptions: {
      columns: Array.from(BALANCE_COL_DEF_COIN, item => item?.field),
      includeHeaders: true,
      includeOutliers: true,
    },
    sortModel: BALANCE_GROUPING_COIN_SORTING,
  },
  account: {
    endpoint: process.env.REACT_APP_API_ASSET_ACCOUNT,
    columns: BALANCE_COL_DEF_ACCOUNT,
    autosizeOptions: {
      columns: Array.from(BALANCE_COL_DEF_ACCOUNT, item => item?.field),
      includeHeaders: true,
      includeOutliers: true,
    },
    sortModel: BALANCE_GROUPING_ACCOUNT_SORTING,
  },
  symbol: {
    endpoint: undefined,
    columns: [],
    autosizeOptions: DEFAULT_GRID_AUTOSIZE_OPTIONS,
    sortModel: [],
  }
}
